import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, MessageCircle, Mail, Calendar, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDayPass } from "@/contexts/DayPassContext";
import { OptimizedImage } from "@/components/OptimizedImage";
import {
    trackActivityModalClose,
    trackActivityLearnMore,
    trackActivityTakeaway,
    trackActivityContact,
} from "@/utils/analytics";

interface Activity {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    image: string;
    extendedDescription?: string[]; // Array of paragraph strings
    dayPassValue?: string; // Day Pass value proposition text
    dayPassValuePrivate?: string; // Additional private booking note (for cinema)
    learnMoreLink?: {
        href: string;
        text: string;
        isExternal?: boolean;
    };
    takeawayLink?: {
        href: string;
        text: string;
        isExternal?: boolean;
    };
    menuImage?: string; // Optional menu image to display
    isComingSoon?: boolean;
    comingSoonBadge?: string; // Optional custom coming soon badge text
}

interface ActivityModalProps {
    isOpen: boolean;
    onClose: () => void;
    activity: Activity | null;
    dataOid?: string;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
    isOpen,
    onClose,
    activity,
    dataOid,
}) => {
    const { t } = useLanguage();
    const { setIsTrayOpen } = useDayPass();
    const [modalOpenTime, setModalOpenTime] = useState<number | null>(null);

    // Track modal open time
    useEffect(() => {
        if (isOpen && activity) {
            setModalOpenTime(Date.now());
        }
    }, [isOpen, activity]);

    if (!activity) return null;

    const handleLearnMore = () => {
        if (activity.learnMoreLink) {
            let linkType: 'internal' | 'external' | 'anchor' = 'internal';
            if (activity.learnMoreLink.isExternal) {
                linkType = 'external';
            } else if (activity.learnMoreLink.href.startsWith('#')) {
                linkType = 'anchor';
            }
            
            trackActivityLearnMore(
                activity.id,
                activity.title,
                linkType,
                activity.learnMoreLink.href
            );
            
            if (activity.learnMoreLink.isExternal) {
                window.open(activity.learnMoreLink.href, '_blank', 'noopener,noreferrer');
            } else if (activity.learnMoreLink.href.startsWith('#')) {
                // Handle anchor links (scroll to section)
                const element = document.querySelector(activity.learnMoreLink.href);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                    onClose();
                }
            } else {
                // Internal navigation
                window.location.href = activity.learnMoreLink.href;
            }
        }
    };

    const handleTakeawayLink = () => {
        if (activity.takeawayLink) {
            trackActivityTakeaway(
                activity.id,
                activity.title,
                activity.takeawayLink.href
            );
            
            if (activity.takeawayLink.isExternal) {
                window.open(activity.takeawayLink.href, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = activity.takeawayLink.href;
            }
        }
    };

    return (
        <AnimatePresence data-oid="activity-modal-presence">
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className="fixed inset-0 bg-black/80 backdrop-blur-xl z-[10000]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            if (activity && modalOpenTime) {
                                const timeSpent = Math.round((Date.now() - modalOpenTime) / 1000);
                                trackActivityModalClose(
                                    activity.id,
                                    activity.title,
                                    'backdrop',
                                    timeSpent
                                );
                            }
                            setModalOpenTime(null);
                            onClose();
                        }}
                        data-oid="modal-backdrop"
                    />

                    {/* Modal Container */}
                    <motion.div
                        className="fixed inset-0 z-[10001] flex items-center justify-center p-4 md:p-8 pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-oid="modal-container"
                    >
                        <motion.div
                            className="bg-white rounded-[2rem] shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col md:flex-row relative"
                            initial={{ scale: 0.9, y: 30, opacity: 0 }}
                            animate={{
                                scale: 1,
                                y: 0,
                                opacity: 1,
                                transition: { type: "spring", damping: 25, stiffness: 300 },
                            }}
                            exit={{ scale: 0.9, y: 30, opacity: 0 }}
                            data-oid="modal-content"
                        >
                            {/* Image Side - 50% width */}
                            <div
                                className="md:w-1/2 h-64 md:h-auto relative overflow-hidden"
                                data-oid="modal-image-container"
                            >
                                <OptimizedImage
                                    src={activity.image}
                                    alt={activity.title}
                                    className="w-full h-full object-cover"
                                    data-oid="modal-image"
                                />
                                <div
                                    className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-white/10"
                                    data-oid="modal-image-overlay"
                                />

                                {/* Coming Soon Badge */}
                                {(activity.isComingSoon || activity.comingSoonBadge) && (
                                    <div className="absolute top-4 left-4 bg-alpine-gold text-charcoal-gray px-4 py-2 rounded-full font-bold text-xs tracking-widest uppercase shadow-lg">
                                        {activity.comingSoonBadge || t("activities.comingSoon")}
                                    </div>
                                )}

                                {/* Mobile Close Button */}
                                <button
                                    onClick={() => {
                                        if (activity && modalOpenTime) {
                                            const timeSpent = Math.round((Date.now() - modalOpenTime) / 1000);
                                            trackActivityModalClose(
                                                activity.id,
                                                activity.title,
                                                'button',
                                                timeSpent
                                            );
                                        }
                                        setModalOpenTime(null);
                                        onClose();
                                    }}
                                    className="absolute top-4 right-4 p-2 bg-black/40 text-white rounded-full md:hidden backdrop-blur-md hover:bg-black/60 transition-colors"
                                    data-oid="mobile-close-btn"
                                >
                                    <X size={20} data-oid="mobile-close-icon" />
                                </button>
                            </div>

                            {/* Content Side - 50% width */}
                            <div
                                className="md:w-1/2 p-6 md:p-10 overflow-y-auto bg-white flex flex-col"
                                data-oid="modal-text-container"
                            >
                                {/* Desktop Close Button */}
                                    <button
                                        onClick={() => {
                                            if (activity && modalOpenTime) {
                                                const timeSpent = Math.round((Date.now() - modalOpenTime) / 1000);
                                                trackActivityModalClose(
                                                    activity.id,
                                                    activity.title,
                                                    'button',
                                                    timeSpent
                                                );
                                            }
                                            setModalOpenTime(null);
                                            onClose();
                                        }}
                                    className="absolute top-6 right-6 p-2 text-gray-300 hover:text-alpine-gold rounded-full transition-colors hidden md:block z-10"
                                        data-oid="desktop-close-btn"
                                    >
                                    <X size={24} data-oid="desktop-close-icon" />
                                    </button>

                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.2 }}
                                    className="space-y-6 flex-grow"
                                        data-oid="modal-inner-content"
                                    >
                                    {/* Subtitle & Title */}
                                        <div>
                                            <p
                                                className="text-alpine-gold font-lato text-xs md:text-sm tracking-[0.3em] uppercase font-bold mb-3"
                                                data-oid="modal-subtitle"
                                            >
                                                {activity.subtitle}
                                            </p>
                                            <h2
                                            className="text-3xl md:text-4xl font-playfair text-gray-900 leading-tight"
                                                data-oid="modal-title"
                                            >
                                                {activity.title}
                                            </h2>
                                        </div>

                                    {/* Divider */}
                                        <div
                                            className="w-12 h-[2px] bg-alpine-gold/30"
                                            data-oid="modal-divider"
                                        />

                                    {/* Extended Service Description */}
                                    {activity.extendedDescription && activity.extendedDescription.length > 0 && (
                                        <div className="space-y-4" data-oid="extended-description">
                                            {activity.extendedDescription.map((paragraph, index) => (
                                                <p
                                                    key={index}
                                                    className="text-gray-600 font-lato text-base leading-relaxed"
                                                    data-oid={`description-paragraph-${index}`}
                                                >
                                                    {paragraph}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {/* Menu Image (if provided) */}
                                    {activity.menuImage && (
                                        <div className="my-6 space-y-3" data-oid="menu-image-container">
                                            <OptimizedImage
                                                src={activity.menuImage}
                                                alt={`${activity.title} Menu`}
                                                className="w-full rounded-xl shadow-md"
                                                data-oid="menu-image"
                                            />
                                            {/* Takeaway Link (for bistro) */}
                                            {activity.takeawayLink && (
                                                <div className="text-center">
                                                    <button
                                                        onClick={handleTakeawayLink}
                                                        className="text-alpine-gold hover:text-alpine-gold/80 font-lato text-sm underline flex items-center gap-1 transition-colors mx-auto"
                                                        data-oid="takeaway-link-btn"
                                                    >
                                                        {activity.takeawayLink.text}
                                                        {activity.takeawayLink.isExternal && (
                                                            <ExternalLink size={14} className="inline" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Day Pass Value Proposition */}
                                    {activity.dayPassValue && (
                                        <div
                                            className="bg-alpine-gold/10 border border-alpine-gold/20 p-4 rounded-xl space-y-3"
                                            data-oid="day-pass-value"
                                        >
                                            <p className="text-gray-700 font-lato text-sm leading-relaxed">
                                                {activity.dayPassValue}
                                            </p>
                                            {activity.dayPassValuePrivate && (
                                                <p className="text-gray-700 font-lato text-sm leading-relaxed font-semibold">
                                                    {activity.dayPassValuePrivate}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Primary CTA - Book Day Pass Button */}
                                    <div className="pt-2" data-oid="primary-cta">
                                        <Button
                                            onClick={() => {
                                                // Get activity category from id
                                                const categoryMap: Record<string, string> = {
                                                    'dining': 'Dining',
                                                    'bistro_dine_in': 'Dining',
                                                    'sauna': 'Wellness',
                                                    'spa': 'Wellness',
                                                    'cinema': 'Entertainment',
                                                    'billiards': 'Entertainment',
                                                    'pool': 'Recreation',
                                                    'sports': 'Recreation',
                                                };
                                                
                                                setIsTrayOpen(true, 'activity_modal', activity.id, activity.title);
                                                if (activity && modalOpenTime) {
                                                    const timeSpent = Math.round((Date.now() - modalOpenTime) / 1000);
                                                    trackActivityModalClose(
                                                        activity.id,
                                                        activity.title,
                                                        'button',
                                                        timeSpent
                                                    );
                                                }
                                                setModalOpenTime(null);
                                                onClose();
                                            }}
                                            className="w-full bg-alpine-gold hover:bg-alpine-gold/90 text-white px-6 py-6 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                                            trackingType="none"
                                            data-oid="book-day-pass-btn"
                                        >
                                            <Calendar size={20} className="mr-2" />
                                            {t("activities.modal.bookDayPass")}
                                        </Button>
                                    </div>

                                    {/* Learn More Link */}
                                    {activity.learnMoreLink && (
                                        <div className="pt-2" data-oid="learn-more-link">
                                            <button
                                                onClick={handleLearnMore}
                                                className="text-alpine-gold hover:text-alpine-gold/80 font-lato text-sm underline flex items-center gap-1 transition-colors"
                                                data-oid="learn-more-btn"
                                                >
                                                {activity.learnMoreLink.text}
                                                {activity.learnMoreLink.isExternal && (
                                                    <ExternalLink size={14} className="inline" />
                                                )}
                                            </button>
                                            </div>
                                    )}

                                    {/* Divider before contact section */}
                                    <div className="pt-4">
                                        <div className="h-px bg-gray-200" data-oid="contact-divider" />
                                    </div>

                                    {/* Compact Contact Options (Bottom) */}
                                    <div className="pt-4 space-y-3" data-oid="contact-section">
                                        <p className="text-gray-500 font-lato text-xs uppercase tracking-widest font-semibold">
                                            {t("activities.modal.contactSupport")}
                                            </p>
                                        <div className="flex flex-wrap gap-4" data-oid="contact-options">
                                                <a
                                                    href="tel:+41338410021"
                                                    onClick={() => trackActivityContact(
                                                        activity.id,
                                                        activity.title,
                                                        'phone',
                                                        '+41 33 841 00 21',
                                                        'activity_modal' // contact_source
                                                    )}
                                                className="flex items-center gap-2 text-gray-600 hover:text-alpine-gold transition-colors group"
                                                    data-oid="phone-link"
                                                >
                                                <Phone size={16} className="text-gray-400 group-hover:text-alpine-gold" />
                                                <span className="font-lato text-sm">+41 33 841 00 21</span>
                                            </a>
                                            <a
                                                href="mailto:info@beatenberg-resortspa.ch"
                                                onClick={() => trackActivityContact(
                                                    activity.id,
                                                    activity.title,
                                                    'email',
                                                    'info@beatenberg-resortspa.ch',
                                                    'activity_modal' // contact_source
                                                )}
                                                className="flex items-center gap-2 text-gray-600 hover:text-alpine-gold transition-colors group"
                                                data-oid="email-link"
                                                        >
                                                <Mail size={16} className="text-gray-400 group-hover:text-alpine-gold" />
                                                <span className="font-lato text-sm">info@beatenberg-resortspa.ch</span>
                                                </a>
                                                <a
                                                    href="https://wa.me/41766220122"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => trackActivityContact(
                                                        activity.id,
                                                        activity.title,
                                                        'whatsapp',
                                                        '+41 76 622 01 22',
                                                        'activity_modal' // contact_source
                                                    )}
                                                className="flex items-center gap-2 text-gray-600 hover:text-[#25D366] transition-colors group"
                                                    data-oid="whatsapp-link"
                                                >
                                                <MessageCircle size={16} className="text-gray-400 group-hover:text-[#25D366]" />
                                                <span className="font-lato text-sm">+41 76 622 01 22</span>
                                                </a>
                                            </div>
                                        </div>
                                    </motion.div>
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ActivityModal;
